import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import { Box, Typography, CircularProgress } from "@mui/material";
import CustomPagination from "@/components/dataTableComponents/CustomPagination"; // Ensure correct path
import TableSearch from "@/pages/payroll/component/table/tableSearch";
import "./DataTable.css"; // Import the CSS file
import AddIcon from "@mui/icons-material/ControlPointSharp";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutlineSharp";

const DataTable = ({
  columns,
  rows,
  checkboxSelection,
  headerCheckboxVisibility,
  onRowClick,
  headerBgColor,
  colvis,
  searchBar,
  index,
  exportOption,
  csvOption,
  hidePagination,
  isExpandable,
  renderExpandableRow,
  onSelectedRowsChange,
  checkboxConditionFn,
  doubleHeader,
  loading = false,
  ...props
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [visibleColumns, setVisibleColumns] = useState(
    columns.map((column) => ({ ...column, visible: true }))
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState([]); // Store selected row data

  const [searchQuery, setSearchQuery] = useState("");

  const handleToggleColumn = (field) => {
    setVisibleColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.field === field
          ? { ...column, visible: !column.visible }
          : column
      )
    );
  };

  const filteredColumns = visibleColumns?.filter((column) => column.visible);

  const modifiedColumns = index
    ? [
      {
        name: "S.No",
        label: "S.No",
        options: {
          customBodyRenderLite: (dataIndex) => {
            return dataIndex + 1 + page * rowsPerPage;
          },
          sort: false,
          viewColumns: true,
        },
      },
      ...filteredColumns.map((col) => {
        return {
          name: col.field,
          label: col.headerName,
          options: {
            ...col.options,
            sort: false,
            viewColumns: true,
            customBodyRender: (value) => {
              // Check if value is a string or number
              const displayValue =
                typeof value === "string" || typeof value === "number"
                  ? value.toString()
                  : ""; // Convert to string if it's a number
              const wordCount = displayValue.split(" ").length;
              const shouldScroll = wordCount > 2; // Check if there are more than 2 words

              return (
                <Typography
                  className={shouldScroll ? "scrollable-cell" : ""}
                  variant="body2" // You can adjust the variant based on your design needs
                  noWrap // This will prevent wrapping
                >
                  {displayValue}
                </Typography>
              );
            },
          },
        };
      }),
    ]
    : filteredColumns.map((col) => {
      return {
        name: col.field,
        label: col.headerName,
        options: { ...col.options, sort: false, viewColumns: true },
      };
    });

  const isRowCheckboxAllowed = checkboxConditionFn || (() => true);

  const handleTableChange = (action, tableState) => {
    if (action === "changePage") {
      setPage(tableState.page);
    } else if (action === "rowSelectionChange") {
      let selectedRowIndices = tableState.selectedRows.data.map(
        (row) => row.index
      );

      // Custom logic: Filter based on `hrSuggestedEmail`
      const filteredIndices = selectedRowIndices.filter((index) => {
        const row = rows[index];
        return isRowCheckboxAllowed(row);
      });
      console.log("filteredIndices - ", filteredIndices);

      setSelectedRows(filteredIndices);
      // const selectedData = filteredIndices.map((index) => rows[index]);
      const selectedData = filteredIndices.map((index) => filteredRows[index]);
      console.log("selectedData - ", selectedData);
      setSelectedRowData(selectedData);

      if (onSelectedRowsChange) {
        onSelectedRowsChange(selectedData);
      }
    }
  };

  // const filteredRows = rows;
  const filteredRows = rows?.filter((row) => {
    if (!searchQuery || !searchQuery.trim()) return true;

    // Check all visible fields in each row
    return filteredColumns.some((column) => {
      const field = column.field;
      const value = row[field];
      return (
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  });

  useEffect(() => {
  setPage(0);
}, [searchQuery]);

  const totalRows = filteredRows?.length;
  const displayedRows = filteredRows?.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  const handlePageChange = (newPage) => {
    setPage(newPage - 1);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(parseInt(newRowsPerPage, 10));
    setPage(0);
  };

  const components = {
    ExpandButton: function (props) {
      const [isExpanded, setIsExpanded] = useState(false);
      const handleClick = () => {
        setIsExpanded(!isExpanded);
      };

      if (props.dataIndex === -1) {
        return null;
      }

      return (
        <div className="expand-button-container" onClick={handleClick}>
          {isExpanded ? <RemoveIcon {...props} /> : <AddIcon {...props} />}
        </div>
      );
    },
  };

  const options = {
    filter: false,
    search: false,
    print: false,
    download: false,
    viewColumns: false,
    expandableRows: isExpandable,
    renderExpandableRow: renderExpandableRow,
    expandableRowsHeader: false, // Hide the expand button in the header
    expandableRowsOnClick: true,
    responsive: "standard",
    // textLabels: {
    //   body: {
    //     noMatch: "No Data Available",
    //   },
    // },
     textLabels: {
    body: {
      noMatch: loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        "No Data Available"
      ),
    },
  },
    customExpandColumn: {
      component: components.ExpandButton,
      props: {},
      preventExpandOnClick: true, // Prevent the header from expanding
    },
    selectableRows: checkboxSelection ? "multiple" : "none",
    // isRowSelectable: (dataIndex) => {
    //   const row = rows[dataIndex];
    //   return !row?.[checkboxConditionKey]; // Dynamically check the field
    // },

    setRowProps: (row, dataIndex) => {
      const rowData = rows[dataIndex];
      const isSelectable = isRowCheckboxAllowed(rowData);

      return {
        className: isSelectable ? "" : "hide-checkbox", // Optional for styling (optional now)
      };
    },
    rowsSelected: selectedRows, // Synchronize selected rows
    pagination: !hidePagination,
    onRowClick: (rowData, rowMeta) => {
      if (onRowClick) onRowClick(rowData, rowMeta);
    },
    rowsPerPage,
    page,
    onTableChange: handleTableChange,
    customFooter: hidePagination
      ? null
      : () => (
        <Box sx={{ mt: 2 }}>
          <CustomPagination
            page={page + 1}
            rowsPerPage={rowsPerPage}
            totalRows={totalRows}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Box>
      ),
    setTableProps: () => {
      return {
        className: "custom-table-root",
      };
    },
    setCellHeaderProps: () => ({}),
    selectToolbarPlacement: "none", // Prevent default select toolbar
  };

  return (
    <>
      {/* <TableSearch
        headers={modifiedColumns}
        handleToggleColumn={handleToggleColumn}
        colvis={colvis}
        visibleColumns={visibleColumns}
        searchBar={searchBar}
        colvisColumns={columns}
        exportOption={exportOption}
        csvOption={csvOption}
      /> */}

      <TableSearch
        headers={modifiedColumns}
        handleToggleColumn={handleToggleColumn}
        colvis={colvis}
        visibleColumns={visibleColumns}
        searchBar={searchBar}
        colvisColumns={columns}
        exportOption={exportOption}
        csvOption={csvOption}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      <div className="custom-table-roots">

 
        {doubleHeader && (
          <table className="double-header-table" style={{ backgroundColor: headerBgColor }}>
            <thead>
              <tr>
                {(() => {
                  const employeeKeys = ["empId"];
                  const employeeCols = filteredColumns?.filter((col) =>
                    employeeKeys.includes(col.field)
                  ).length;
                  const shiftCols = filteredColumns.length - employeeCols;

                  const employeeWidth = (employeeCols / filteredColumns.length) * 100;
                  const shiftWidth = (shiftCols / filteredColumns.length) * 100;

                  return (
                    <>
                      <th colSpan={employeeCols} style={{ width: `${employeeWidth}%`, padding: "8px", textAlign: "center", borderRight: "1px solid #ccc" }}>
                        {doubleHeader.topHeaders[0]}
                      </th>
                      <th colSpan={shiftCols} style={{ width: `${shiftWidth}%`, textAlign: "center", padding: "8px" }}>
                        {doubleHeader.topHeaders[1]}
                      </th>
                    </>
                  );
                })()}
              </tr>
            </thead>
          </table>
        )}

        <MUIDataTable
          title=""
          data={displayedRows}
          columns={modifiedColumns}
          options={options}
          className="custom-table-root"
          {...props}
          components={components}
          loading={loading}
        />
       
      </div>
    </>
  );
};

export default DataTable;
